/**
 * MoneyGram Service Options Service Implementation
 * Handles service configuration, fees, and available options
 */

import type { AxiosInstance } from "axios";
import type { ServiceOptionsService } from "../contracts/service-options.contract";
import type { RequestConfig } from "../types/common";
import type {
	GetServiceOptionsRequest,
	GetServiceOptionsResponse,
	ServiceOption,
} from "../types/moneygram";

export class MoneyGramServiceOptionsService implements ServiceOptionsService {
	constructor(private readonly httpClient: AxiosInstance) {}

	async getServiceOptions(
		request: GetServiceOptionsRequest,
		config?: RequestConfig,
	): Promise<GetServiceOptionsResponse> {
		try {
			const params = new URLSearchParams({
				operation: request.operation,
				...(request.assetCode && { asset_code: request.assetCode }),
				...(request.userSelectedCountry && {
					userSelectedCountry: request.userSelectedCountry,
				}),
			});

			const response = await this.httpClient.get(
				`/config-data/serviceoptions?${params.toString()}`,
				{
					signal: config?.signal,
					timeout: config?.timeout,
				},
			);

			return this.transformServiceOptionsResponse(response.data);
		} catch (error) {
			throw this.handleApiError(error, "Failed to fetch service options");
		}
	}

	async getServiceOptionsByTransaction(
		transactionId: string,
		config?: RequestConfig,
	): Promise<ServiceOption[]> {
		try {
			const response = await this.httpClient.get(
				`/transactions/${transactionId}/serviceoptions`,
				{
					signal: config?.signal,
					timeout: config?.timeout,
				},
			);

			return this.transformServiceOptionsArray(response.data);
		} catch (error) {
			throw this.handleApiError(
				error,
				`Failed to fetch service options for transaction ${transactionId}`,
			);
		}
	}

	/**
	 * Transform service options API response
	 */
	private transformServiceOptionsResponse(
		apiData: unknown,
	): GetServiceOptionsResponse {
		const data = apiData as Record<string, unknown>;

		const serviceOptions = this.transformServiceOptionsArray(
			data.serviceOptions || data.options || [],
		);

		return {
			serviceOptions,
			exchangeRates: (data.exchangeRates || data.rates || {}) as Record<
				string,
				number
			>,
			updatedAt: String(
				data.updatedAt || data.timestamp || new Date().toISOString(),
			),
			withdrawCountry: String(data.withdrawCountry || ""),
			cashOutCountries: Array.isArray(data.cashOutCountries)
				? data.cashOutCountries
				: [],
			region: data.region ? String(data.region) : undefined,
		};
	}

	/**
	 * Transform service options array
	 */
	private transformServiceOptionsArray(apiData: unknown): ServiceOption[] {
		//TODO: Update to actually match service response
		if (!Array.isArray(apiData)) {
			// Unexpected service options API response format
			return [];
		}

		return apiData.map((item: Record<string, unknown>) => ({
			id: String(item.id || item.optionId || ""),
			operation: (item.operation as "withdraw" | "deposit") || "withdraw",
			assetCode: String(item.assetCode || item.asset_code || "USDC"),
			payoutOptionCode: String(
				item.payoutOptionCode || item.payout_option_code || "",
			),
			payoutCurrencyCodes: Array.isArray(item.payoutCurrencyCodes)
				? item.payoutCurrencyCodes.map((c) => String(c))
				: Array.isArray(item.payout_currency_codes)
					? item.payout_currency_codes.map((c) => String(c))
					: [],
			localCurrencyCode: String(
				item.localCurrencyCode || item.local_currency_code || "",
			),
			isIndicativeRateAvailable: Boolean(
				item.isIndicativeRateAvailable ??
					item.is_indicative_rate_available ??
					false,
			),
			availableCountries: Array.isArray(item.availableCountries)
				? item.availableCountries.map((c) => String(c))
				: Array.isArray(item.countries)
					? item.countries.map((c) => String(c))
					: [],
			fees: {
				fixed:
					typeof item.fees === "object" && item.fees !== null
						? Number((item.fees as Record<string, unknown>).fixed) || undefined
						: Number(item.fixedFee) || undefined,
				percentage:
					typeof item.fees === "object" && item.fees !== null
						? Number((item.fees as Record<string, unknown>).percentage) ||
							undefined
						: Number(item.percentageFee) || undefined,
				minimum:
					typeof item.fees === "object" && item.fees !== null
						? Number((item.fees as Record<string, unknown>).minimum) ||
							undefined
						: Number(item.minFee) || undefined,
				maximum:
					typeof item.fees === "object" && item.fees !== null
						? Number((item.fees as Record<string, unknown>).maximum) ||
							undefined
						: Number(item.maxFee) || undefined,
			},
			limits: {
				minimum:
					typeof item.limits === "object" && item.limits !== null
						? Number((item.limits as Record<string, unknown>).minimum) ||
							Number(item.minAmount) ||
							0
						: Number(item.minAmount) || 0,
				maximum:
					typeof item.limits === "object" && item.limits !== null
						? Number((item.limits as Record<string, unknown>).maximum) ||
							Number(item.maxAmount) ||
							Number.MAX_SAFE_INTEGER
						: Number(item.maxAmount) || Number.MAX_SAFE_INTEGER,
			},
		}));
	}

	/**
	 * Handle and transform API errors
	 */
	private handleApiError(error: unknown, defaultMessage: string): Error {
		if (error instanceof Error) {
			return new Error(`${defaultMessage}: ${error.message}`);
		}
		return new Error(defaultMessage);
	}
}
